import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, Link, MenuItem, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Project, ProjectStatus } from '../../interfaces/project';
import { ProjectService } from '../../services/project.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import {
    ProjectFormDialog, ProjectStatusDialog, actionLabel, nextStatuses, statusColor, statusLabel,
} from './ProjectDialogs';

interface Filters { status: '' | ProjectStatus; search: string }

const PAGE_SIZE = 50;

/** P1-A: the project register (PROJECT_VIEW). Managers reach their own projects from "My projects". */
const ProjectList: React.FC = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const canManage = usePermission(PERMISSIONS.PROJECT_MANAGE);
    const [status, setStatus] = useState<'' | ProjectStatus>('');
    const [search, setSearch] = useState('');
    const [applied, setApplied] = useState<Filters>({ status: '', search: '' });
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState<Project[]>([]);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState<Project | null | undefined>(undefined);
    const [statusTarget, setStatusTarget] = useState<{ project: Project; to: ProjectStatus } | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await ProjectService.list({
                status: applied.status || undefined, search: applied.search.trim() || undefined, page, size: PAGE_SIZE,
            });
            setRows(res.content ?? []);
            setTotalPages(res.totalPages ?? 0);
        } catch (err) {
            toast.showApiError(err, 'Failed to load projects');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applied, page]);

    useEffect(() => { load(); }, [load]);

    const apply = () => { setPage(0); setApplied({ status, search }); };

    return (
        <Box data-help-page="projects.list.page" sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Typography variant="h5">Projects</Typography>
                {canManage && (
                    <Button data-help="projects.list.new" variant="contained" startIcon={<AddIcon />} onClick={() => setEditing(null)} data-testid="project-new">
                        New project
                    </Button>
                )}
            </Box>
            <Box data-help-section="projects.list.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
                <TextField data-help="projects.list.status" select label="Status" size="small" value={status} onChange={e => setStatus(e.target.value as '' | ProjectStatus)}
                    sx={{ flex: '1 1 160px', maxWidth: 220 }}>
                    <MenuItem value="">All</MenuItem>
                    {(Object.keys(statusLabel) as ProjectStatus[]).map(s => <MenuItem key={s} value={s}>{statusLabel[s]}</MenuItem>)}
                </TextField>
                <TextField data-help="projects.list.search" label="Search code, name, customer or manager" size="small" value={search}
                    onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') apply(); }}
                    sx={{ flex: '1 1 260px', maxWidth: 420 }} inputProps={{ 'data-testid': 'project-search' }} />
                <Button data-help="projects.list.apply" variant="outlined" onClick={apply} disabled={loading}>Apply</Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
                <Table data-help-section="projects.list.table" size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="projects.list.col.code">Code</TableCell><TableCell data-help="projects.list.col.name">Name</TableCell><TableCell data-help="projects.list.col.customer">Customer</TableCell>
                            <TableCell data-help="projects.list.col.manager">Manager</TableCell><TableCell data-help="projects.list.col.status">Status</TableCell><TableCell data-help="projects.list.col.start">Start</TableCell><TableCell data-help="projects.list.col.end">End</TableCell>
                            <TableCell data-help="projects.list.col.budget" align="right">Budget cost</TableCell><TableCell data-help="projects.list.col.actual" align="right">Actual cost</TableCell>
                            <TableCell data-help="projects.list.col.used" align="right">% used</TableCell>{canManage && <TableCell data-help="projects.list.col.actions">Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow><TableCell colSpan={canManage ? 11 : 10}>{loading ? 'Loading...' : 'No projects yet.'}</TableCell></TableRow>
                        )}
                        {rows.map(p => (
                            <TableRow key={p.id} hover data-testid="project-row">
                                <TableCell>
                                    <Link data-help="projects.list.action.open" component="button" onClick={() => navigate(`/accounting/projects/${p.id}`)}>{p.code}</Link>
                                </TableCell>
                                <TableCell>{p.name}</TableCell>
                                <TableCell>{p.customerName ?? '—'}</TableCell>
                                <TableCell>{p.managerName ?? '—'}</TableCell>
                                <TableCell><Chip size="small" label={statusLabel[p.status]} color={statusColor[p.status]} /></TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.startDate ? formatDate(p.startDate) : '—'}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {p.closedOn ? `Closed ${formatDate(p.closedOn)}` : p.endDate ? formatDate(p.endDate) : '—'}
                                </TableCell>
                                <TableCell align="right">{p.budgetCost != null ? formatMoney(p.budgetCost) : '—'}</TableCell>
                                <TableCell align="right">{formatMoney(p.actualCost ?? 0)}</TableCell>
                                <TableCell align="right" sx={{ color: (p.costUsedPct ?? 0) > 100 ? 'error.main' : undefined }}>
                                    {p.costUsedPct != null ? `${p.costUsedPct}%` : '—'}
                                </TableCell>
                                {canManage && (
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Button data-help="projects.list.action.edit" size="small" onClick={() => setEditing(p)}>Edit</Button>
                                        {nextStatuses(p.status).map(to => (
                                            <Button data-help="projects.list.action.status" key={to} size="small" color={to === 'CLOSED' ? 'warning' : 'primary'}
                                                onClick={() => setStatusTarget({ project: p, to })}>
                                                {actionLabel(p.status, to)}
                                            </Button>
                                        ))}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination count={totalPages} page={page + 1} onChange={(_, p) => setPage(p - 1)} />
                </Box>
            )}
            <ProjectFormDialog open={editing !== undefined} project={editing ?? null} onClose={() => setEditing(undefined)}
                onSaved={() => { setEditing(undefined); load(); }} />
            <ProjectStatusDialog project={statusTarget?.project ?? null} target={statusTarget?.to ?? null}
                onClose={() => setStatusTarget(null)} onDone={() => { setStatusTarget(null); load(); }} />
        </Box>
    );
};

export default ProjectList;
